import { useState, useEffect, useRef } from "react";
import localization from "../../../assets/localization";
import ChatService from "../../../front-services/ChatService";
import Analytics from "../../../utils/Analytics";
import { getVariant } from "../../../utils/Experiment";
import { wasWidgetOpened } from "../../../utils/WidgetOpenState";

const typingSpeed: number = 80;
const pauseDuration: number = 2500;
// Rotation cadence for the bottom (dynamic) pill. Slow enough to be read,
// fast enough to show the assistant's range while the guest hesitates.
const pillRotationMs: number = 4500;

// Backoffice-configurable copy (per locale) with localized fallbacks.
function getTeaserCopy(assistantChatConfiguration: any, language: string) {
  const locale = localization.get(language);
  const fixed =
    assistantChatConfiguration?.teaserPillFixed || locale.teaser_pill_fixed;
  const rotating: string[] =
    assistantChatConfiguration?.teaserPillsRotating?.length > 0
      ? assistantChatConfiguration.teaserPillsRotating
      : locale.teaser_pills_rotating;
  return { fixed, rotating };
}

export default function ChatTooltip({
  isMobile,
  language,
  assistantId = "",
  assistantChatConfiguration,
  onTeaserClick,
}: any) {
  const [displayedText, setDisplayedText] = useState<string>("");
  const [pillIndex, setPillIndex] = useState<number>(0);
  // The stack animates in like messages arriving: fixed pill first, the
  // rotating one a beat later.
  const [showRotating, setShowRotating] = useState<boolean>(false);

  const sentenceIndexRef = useRef<number>(0);

  // Open-rate experiment: variant B swaps the decorative typewriter tooltip
  // for two clickable question pills (AIKosmo-style stack). IFH assistants
  // keep the legacy tooltip (their teaser copy is brand-mandated).
  const isBVariant =
    getVariant() === "B" && !ChatService.isIodah(assistantId) && onTeaserClick;
  // Mobile: once the guest has opened the widget we stop showing the teaser
  // pills for good (persisted across reloads) — no proactive nag on close or
  // reload. Desktop keeps the pills. The launcher stays either way.
  const pillsSuppressed = isBVariant && isMobile && wasWidgetOpened();
  const isPillVariant = isBVariant && !pillsSuppressed;

  useEffect(() => {
    // Deferred a tick: child effects run before the parent's, so tracking
    // synchronously here would race Analytics.init in Chat.tsx and be dropped.
    if (pillsSuppressed) return; // nothing is shown, so nothing to track
    const timer = setTimeout(() => {
      Analytics.track("teaser_shown", {
        type: isPillVariant ? "pills" : "tooltip",
      });
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isPillVariant) return;
    const { rotating } = getTeaserCopy(assistantChatConfiguration, language);
    const stagger = setTimeout(() => setShowRotating(true), 900);
    const timer = setInterval(
      () => setPillIndex((prev) => (prev + 1) % rotating.length),
      pillRotationMs
    );
    return () => {
      clearTimeout(stagger);
      clearInterval(timer);
    };
  }, [isPillVariant, language, assistantChatConfiguration]);

  useEffect(() => {
    if (isPillVariant || pillsSuppressed) return;
    let localeSentences = ChatService.isIodah(assistantId)
      ? ["Welcome to The First Hotels.", "How may I help you today?"]
      : localization.get(language).tooltip_sentences;
    let isMounted = true;

    const runTypingEffect = async () => {
      while (isMounted) {
        const sentence = localeSentences[sentenceIndexRef.current];
        setDisplayedText("");
        const generator = typingGenerator(sentence);

        for (const partialText of generator) {
          if (partialText === "PAUSE") {
            await new Promise((resolve) => setTimeout(resolve, pauseDuration));
            break;
          }
          setDisplayedText(partialText);
          await new Promise((resolve) => setTimeout(resolve, typingSpeed));
        }

        sentenceIndexRef.current =
          (sentenceIndexRef.current + 1) % localeSentences.length;
      }
    };

    runTypingEffect();

    return () => {
      isMounted = false;
    };
  }, [language, assistantId, isPillVariant, pillsSuppressed]);

  function* typingGenerator(sentence: string): Generator<string> {
    let text = "";
    const characters = Array.from(sentence); // Handles emojis as single entities
    for (const char of characters) {
      text += char;
      yield text;
    }
    yield "PAUSE";
  }

  const pillClick = (event: any, text: string, slot: string) => {
    // The teaser lives inside the launcher: without this the click would
    // also bubble into toggleChatWidget and double-fire.
    event.stopPropagation();
    Analytics.track("teaser_click", { text, slot });
    onTeaserClick(text);
  };

  // Mobile after first open: no pills, no fallback tooltip — just the launcher.
  if (pillsSuppressed) return null;

  if (isPillVariant) {
    const { fixed, rotating } = getTeaserCopy(
      assistantChatConfiguration,
      language
    );
    const rotatingText = rotating[pillIndex % rotating.length];
    return (
      <div
        className={isMobile ? "chat-tooltip-mobile" : "chat-tooltip-desktop"}
        style={{ pointerEvents: "none" }}
      >
        <button
          className="chat-teaser-pill"
          onClick={(event) => pillClick(event, fixed, "fixed")}
        >
          {fixed} <span className="chat-teaser-pill-arrow">→</span>
        </button>
        {showRotating && (
          <button
            key={rotatingText}
            className="chat-teaser-pill chat-teaser-pill-rotating"
            onClick={(event) => pillClick(event, rotatingText, "rotating")}
          >
            {rotatingText} <span className="chat-teaser-pill-arrow">→</span>
          </button>
        )}
      </div>
    );
  }

  return isMobile ? (
    <div className="chat-tooltip-mobile">
      <div className="chat-tooltip-text-mobile">{displayedText}</div>
    </div>
  ) : (
    <div className="chat-tooltip-desktop">
      <div className="chat-tooltip-text-desktop">{displayedText}</div>
    </div>
  );
}
